/**
 * Determines whether dark or light text has better contrast
 * against a given hex background colour.
 * @param {string} hex - Hex colour string (e.g. "#aabbcc" or "abc")
 * @param {["dark", "light"]} returnValueLabels - Labels to return for dark and light text respectively
 * @returns {"dark" | "light"}
 */
export function bestContrast<ReturnValues extends [string, string]>(
  hex: string,
  returnValueLabels: ReturnValues,
): ReturnValues[number] {
  // Normalize hex string
  let cleanHex = hex.replace(/^#/, '');
  if (cleanHex.length === 3) {
    cleanHex = cleanHex
      .split('')
      .map(c => c + c)
      .join('');
  }

  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;

  // Apply gamma correction
  const [R, G, B] = [r, g, b].map(c => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)));

  // Relative luminance
  const luminance = 0.2126 * R + 0.7152 * G + 0.0722 * B;

  // Compare contrast ratios with black (0) and white (1)
  const contrastWithBlack = (luminance + 0.05) / 0.05;
  const contrastWithWhite = 1.05 / (luminance + 0.05);

  return contrastWithBlack > contrastWithWhite ? returnValueLabels[0] : returnValueLabels[1];
}
